import { ASSISTANT_CHIP_DETAIL_MAX, type BudgetExcess, type Problem } from "@cloudserver/contracts";
import { type AnyRouter, Link, useParams, useRouter } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ApiProblem } from "../api/client.js";
import { AskAssistantButton, type AskRequest } from "../assistant/ask.js";
import { useShownError } from "../assistant/page-errors.js";
import { money } from "./format.js";
import { useInOverlay } from "./overlay.js";
import { Alert } from "./primitives.js";

/**
 * The text a person reads for a problem: its specific reason when we have a
 * translation for it, else the general meaning of its code. The API's English
 * `detail` is never shown; it stays in logs and developer tools.
 */
export function problemText(t: TFunction, problem: Pick<Problem, "code" | "reason">): string {
  if (problem.reason) {
    const specific = t(`errors.reasons.${problem.reason}`, { defaultValue: "" });
    if (specific) return specific;
  }
  return t(`errors.codes.${problem.code}`);
}

/** The longest short name of an error on its chip. */
const SHORT_ERROR = 48;

/**
 * The short name of an error for its chip: the first clause of the text the
 * panel shows («Автопилот сейчас недоступен» of «Автопилот сейчас
 * недоступен. Попробуйте позже.»), cut on a word.
 */
export function shortErrorLabel(text: string): string {
  const first = (text.split(/[.!?;:](?:\s|$)|\s[—–]\s/)[0] ?? text).trim() || text.trim();
  if (first.length <= SHORT_ERROR) return first;
  const cut = first.slice(0, SHORT_ERROR - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > SHORT_ERROR / 2 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/** The text the panel showed, as an error chip's detail carries it first. */
const SHOWN = /^the panel showed: "((?:[^"\\]|\\.)*)"/;

/** The full text of an error chip (its tooltip and the line a tap opens), or null. */
export function errorChipText(detail: string | null | undefined): string | null {
  const found = detail ? SHOWN.exec(detail) : null;
  if (!found?.[1]) return null;
  try {
    return JSON.parse(`"${found[1]}"`) as string;
  } catch {
    return found[1];
  }
}

/**
 * The chip and the first question of «Спросить ассистента» about a problem
 * (docs/assistant.md): the chip says «Ошибка: <short name>»; the whole text
 * goes to its detail, which the model reads and the chip shows on a tap.
 */
export function errorQuestion(t: TFunction, problem: Problem): AskRequest {
  const text = problemText(t, problem);
  return {
    chip: {
      kind: "error",
      resourceId: problem.requestId,
      label: t("assistant.chips.error", { label: shortErrorLabel(text) }).slice(0, 200),
      detail: [
        `the panel showed: ${JSON.stringify(text)}`,
        `code ${problem.code}`,
        problem.reason ? `reason ${problem.reason}` : null,
        `HTTP ${problem.status}`,
        `request id ${problem.requestId}`,
        problem.detail ? `the API's detail: ${problem.detail}` : null,
      ]
        .filter(Boolean)
        .join("; ")
        .slice(0, ASSISTANT_CHIP_DETAIL_MAX),
    },
    question: t("assistant.ask.error"),
  };
}

/** Shows an error in plain language; API errors keep their request id for support. */
export function ErrorMessage({ error }: { error: unknown }) {
  const { t } = useTranslation();
  // Inside a dialog the chat could not be reached (the dialog holds the focus): no button there.
  const inOverlay = useInOverlay();
  // The sign-in page renders before the router exists, and the chat needs the page's route.
  const inRouter = (useRouter({ warn: false }) as AnyRouter | null) !== null;
  const problem = error instanceof ApiProblem ? error.problem : null;
  useShownError(problem && !problem.fields?.length ? (problem.reason ?? problem.code) : null);
  if (!error) return null;
  if (error instanceof ApiProblem && error.problem) {
    // A problem that only concerns form fields is shown next to those fields.
    if (error.problem.fields?.length) return null;
    if (error.problem.reason === "budget_exceeded" && error.problem.budget)
      return <BudgetExceeded excess={error.problem.budget} requestId={error.problem.requestId} />;
    // The assistant's own refusals are not something to ask it about.
    const askable = inRouter && !inOverlay && !error.problem.reason?.startsWith("assistant_");
    return (
      <Alert tone="danger">
        <p>{problemText(t, error.problem)}</p>
        <p className="mt-1 flex flex-wrap items-center gap-x-3 text-xs">
          <span className="opacity-80">
            {t("common.requestId", { id: error.problem.requestId })}
          </span>
          {askable && (
            <AskAssistantButton request={errorQuestion(t, error.problem)} className="-my-1" />
          )}
        </p>
      </Alert>
    );
  }
  if (error instanceof DOMException && error.name === "NotAllowedError") {
    return <Alert tone="warn">{t("signIn.passkeyCancelled")}</Alert>;
  }
  return <Alert tone="danger">{t("common.unexpected")}</Alert>;
}

/**
 * A change refused because the month's forecast would go over a hard
 * budget (docs/budgets.md): by how much, and the way to ask for more.
 */
function BudgetExceeded({ excess, requestId }: { excess: BudgetExcess; requestId: string }) {
  const { t, i18n } = useTranslation();
  const { projectId } = useParams({ strict: false });
  const fmt = (amount: string) => money(amount, excess.currency, i18n.language);
  return (
    <Alert tone="danger">
      <p>{t("errors.reasons.budget_exceeded")}</p>
      <p className="mt-1">
        {t(excess.rule === "new" ? "budgets.exceededNew" : "budgets.exceededChange", {
          over: fmt(excess.over),
          forecast: fmt(excess.forecastAfter),
          budget: fmt(excess.budget),
          limit: fmt(excess.limit),
          margin: excess.changeMargin,
        })}
      </p>
      {projectId && (
        <Link
          to="/projects/$projectId/costs"
          params={{ projectId }}
          search={{ raise: true }}
          className="mt-2 inline-flex min-h-6 items-center font-medium underline underline-offset-2"
        >
          {t("budgets.requestRaise")}
        </Link>
      )}
      <p className="mt-1 text-xs opacity-80">{t("common.requestId", { id: requestId })}</p>
    </Alert>
  );
}

/** The translated message of the invalid field, if the problem points at it. */
export function fieldError(error: unknown, field: string, t: TFunction): string | undefined {
  if (!(error instanceof ApiProblem)) return undefined;
  const found = error.problem?.fields?.find((f) => f.path[0] === field);
  if (!found) return undefined;
  const specific = found.reason ? t(`errors.reasons.${found.reason}`, { defaultValue: "" }) : "";
  return specific || t("errors.fieldInvalid");
}
